import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useSocketEvent } from '../context/SocketContext';
import { SkipForward, Megaphone } from 'lucide-react';

// Shared "Now Serving" token board, shown on both the doctor and
// receptionist dashboards/queue pages so everyone always sees the same
// live token number - like a number display at a clinic reception desk.
// Only the doctor gets the "Next Patient" button; the receptionist sees
// a read-only version of the same display.
export default function NowServingDisplay({ showNextButton = false, onAdvanced }) {
  const [nowServing, setNowServing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calling, setCalling] = useState(false);
  const { showToast } = useToast();
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const res = await api.get('/patients/now-serving');
      setNowServing(res.data.nowServing);
    } catch (err) {
      // Silent - this is a small supplementary widget, not worth a toast on every poll
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useSocketEvent('queue:now-serving', (payload) => {
    setNowServing(payload?.nowServing || null);
  });

  async function handleCallNext() {
    setCalling(true);
    try {
      const res = await api.post('/patients/call-next');
      setNowServing(res.data.nowServing);
      showToast(res.data.message, 'success');
      onAdvanced && onAdvanced(res.data.patient);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not call the next patient.', 'error');
    } finally {
      setCalling(false);
    }
  }

  if (loading) {
    return <div className="skeleton" style={{ height: 92, borderRadius: 12 }} />;
  }

  return (
    <div
      className="card"
      style={{
        padding: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        background: 'linear-gradient(135deg, var(--color-primary-soft), var(--color-surface))',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 12,
            background: 'var(--color-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Megaphone size={24} color="white" />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-ink-faint)' }}>
            Now Serving
          </div>
          {nowServing && nowServing.tokenNumber ? (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 32, fontWeight: 800, color: 'var(--color-primary)', lineHeight: 1 }}>
                #{nowServing.tokenNumber}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{nowServing.patientName}</div>
            </div>
          ) : (
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-ink-faint)' }}>No patient called yet</div>
          )}
        </div>
      </div>

      {showNextButton && user?.role === 'doctor' && (
        <button className="btn btn-primary" onClick={handleCallNext} disabled={calling}>
          <SkipForward size={16} />
          {calling ? 'Calling...' : 'Next Patient'}
        </button>
      )}
    </div>
  );
}
